import { Dispatch, useContext, useEffect, useState } from 'react';
import { EmailFolderInfo, PageWrapper } from '../../Types/MailT';
import { MailS } from '../MailS';
import { MessageAction, MessageDTO } from '../../Types/MessageT';
import { ProfileContext } from '../Context/ProfileContext';
import { MessageContext } from '../Context/MessageContextV2';

// eslint-disable-next-line import/prefer-default-export, @typescript-eslint/no-unused-vars
export const useImapFolderMessages = (
  openedFolder?: EmailFolderInfo,
  openFolder?: Dispatch<EmailFolderInfo>,
  searchQuery?: string,
) => {
  const [pageNumber, setPageNumber] = useState(0);
  const { changeByAction } = useContext(MessageContext);
  const [loading, setLoading] = useState(false);
  const [trigger, setTrigger] = useState<number>();
  const { unreadMessagesProps } = useContext(ProfileContext);
  const [changingSeenFlag, setChangingSeenFlag] = useState(false);
  const [page, setPage] = useState<PageWrapper | null>();
  const [selectedMessage, openMessage] = useState<MessageDTO>();
  const [loadingMessage, setLoadingMessage] = useState(false);
  const { increment, decrement } = unreadMessagesProps;

  useEffect(() => {
    if (openedFolder) {
      const { configId, fullName } = openedFolder;
      if (configId && fullName) {
        setLoading(true);
        MailS.getMessagesIMAP({
          folderName: fullName,
          pageNumber,
          configId,
          query: searchQuery,
        })
          .then(setPage)
          .finally(() => setLoading(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, pageNumber, searchQuery]);

  useEffect(() => {
    if (page && window.innerWidth > 700) {
      selectMessage(page.messages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (openedFolder) {
      if (pageNumber === 0) {
        refresh();
      } else {
        setPageNumber(0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedFolder?.configId, openedFolder?.fullName]);

  const refresh = () => setTrigger(Date.now());
  const { pages, count, messages } = page ?? { pages: 0, count: 0, messages: [] };

  const changeOpenedFolderUnreadCount = (delta: number) => {
    if (openedFolder && openFolder) {
      const unreadMessages = Math.max((openedFolder.unreadMessages ?? 0) + delta, 0);
      openFolder({ ...openedFolder, unreadMessages });
    }
  };

  const selectMessage = (msg?: MessageDTO) => {
    if (msg) {
      openMessage(msg);
      loadFullMessage(msg.id ?? msg.stringId);
    } else {
      openMessage(undefined);
    }
  };

  const loadFullMessage = async (id: number | string) => {
    const { configId, fullName } = openedFolder ?? {};
    if (configId && fullName) {
      setLoadingMessage(true);
      const fullMessage = await MailS.getMessageIMAP(id, configId, fullName);
      setLoadingMessage(false);
      if (fullMessage) {
        openMessage(fullMessage);
      }
    }
  };

  const changeSeenFlag = async (read: boolean) => {
    if (selectedMessage) {
      setChangingSeenFlag(true);
      const { id, stringId, imap, fullNameFolder, outboundConfigId } = selectedMessage;
      if (imap && fullNameFolder && outboundConfigId) {
        const ok = await MailS.changeMessageSeenFlagIMAP(id ?? stringId, outboundConfigId, fullNameFolder, read);
        if (ok) {
          updateMessage({ ...selectedMessage, readTimestamp: read ? new Date() : null });
          const wasRead = !!selectedMessage.readTimestamp;
          if (read && !wasRead) {
            decrement();
            changeOpenedFolderUnreadCount(-1);
          } else if (!read && wasRead) {
            increment();
            changeOpenedFolderUnreadCount(1);
          }
        }
      }
      setChangingSeenFlag(false);
    }
  };

  const markMessagesInFolderAsRead = async () => {
    const { configId, fullName } = openedFolder ?? {};
    const unreadMessages = messages.filter(({ id, stringId, readTimestamp }) => (id || stringId) && !readTimestamp);
    if (!configId || !fullName || unreadMessages.length === 0) {
      return;
    }

    setChangingSeenFlag(true);
    const readTimestamp = new Date();
    const ids = unreadMessages.map(({ id, stringId }) => `${id ?? stringId}`);
    const ok = await MailS.changeMessagesSeenFlagIMAP(ids, configId, fullName, true);
    if (ok) {
      unreadMessages.forEach(() => decrement());
      changeOpenedFolderUnreadCount(-unreadMessages.length);
      setPage({
        pages,
        count,
        messages: messages.map((m) =>
          (m.id || m.stringId) && ids.includes(`${m.id ?? m.stringId}`) ? { ...m, readTimestamp } : m,
        ),
      });
      if (selectedMessage && ids.includes(`${selectedMessage.id ?? selectedMessage.stringId}`)) {
        openMessage({ ...selectedMessage, readTimestamp });
      }
    }
    setChangingSeenFlag(false);
  };

  const updateMessage = (msg: MessageDTO) => {
    openMessage(msg);
    if (page) {
      const { messages: pageMessages } = page;
      const tmp = pageMessages.map((m) =>
        (m.id && m.id === msg.id) || (m.stringId && m.stringId === msg.stringId) ? msg : m,
      );
      setPage({ ...page, messages: tmp });
    }
  };

  const prepareMessage = async (selectedAction: MessageAction) => {
    if (selectedMessage) {
      changeByAction(selectedMessage.id, selectedAction, selectedMessage);
    }
  };

  return {
    loading,
    setPageNumber,
    changeSeenFlag,
    markMessagesInFolderAsRead,
    changingSeenFlag,
    pageNumber,
    totalCount: count,
    pageCount: pages,
    count,
    messages,
    refresh,
    selectMessage,
    selectedMessage,
    loadingMessage,
    prepareMessage,
  };
};
